import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { DialogOpenEvent } from '@theme/dialog';
import { runGiftOperation, registerPrimaryGiftGuard, verifyItemGiftsForCheckout } from '@theme/item-gifts';

const SOURCE = 'choice-gift-picker';

// Presentation settings never participate in gift eligibility or discount checks.
const DEFAULT_COPY = {
  "header_select_one": "Select your free {reward} in cart",
  "header_select_many": "Select your {count} free {reward} in cart",
  "header_next": "Add {needed}{more} {trigger} for {award} free {reward}",
  "header_added": "{count} free {reward} added",
  "header_unlocked": "{count} unlocked",
  "header_reward": "+{count} {reward}",
  "header_complete": "All unlocked",
  "picker_title": "Choose your free {reward}",
  "picker_done": "Free {reward} added",
  "count_available": "{count} to choose",
  "count_selected": "{selected} / {earned} selected",
  "qualifies_one": "With a qualifying {trigger}",
  "qualifies_many": "With {count} qualifying {trigger}",
  "picker_help": "Add {needed} {trigger} to choose your free {reward}.",
  "change_help": "Choose which {reward} to change.",
  "selected_item": "{count} selected · Free",
  "product_trigger_one": "Free {reward} with this {trigger}",
  "product_trigger_many": "Buy {buy} {trigger}, choose {get} free {reward}",
  "product_reward_one": "Free with a {trigger}",
  "product_reward_many": "Free when you buy {buy} {trigger}",
  "product_limit": "Up to {count} {gift} per order",
  "badge_trigger_one": "Free {reward}",
  "badge_trigger_many": "Free {get} {reward}",
  "badge_trigger_group": "Buy {buy}, get {get} free {reward}",
  "badge_reward_many": "Free with {buy} {trigger}",
  "choose": "Choose",
  "view": "View",
  "change": "Change {reward}",
  "close": "Close",
  "add": "Add",
  "remove": "Remove",
  "more": "More choices",
  "load": "Load choices",
  "retry": "Retry gift check",
  "shop": "Shop {trigger}",
  "view_gifts": "View {reward}",
  "choose_this": "Choose this {reward}",
  "choose_a": "Choose a {reward}",
  "option": "Option",
  "one_size": "One size",
  "sold_out": "Sold out",
  "availability": "Checking availability…",
  "loading": "Loading available choices…",
  "checking": "Checking your cart…",
  "added": "Gift added.",
  "removed": "Gift removed.",
  "empty_page": "No choices on this page. View more choices below.",
  "empty_catalog": "No gift choices are available right now. Please check back soon.",
  "verify_error": "We couldn’t verify your gift. Please try again, or remove it in your cart before checkout.",
  "charged_removed": "This gift could not be made free with your current offers. It was removed so you won’t be charged. You can try again after changing your discount.",
  "excess_removed": "Your gift allowance changed. The most recently chosen extra gift was removed.",
  "ineligible_removed": "A gift that no longer qualifies was removed from your cart.",
  "unavailable": "This choice is unavailable. Please choose another style.",
  "no_allowance": "Add a qualifying item to earn another gift.",
  "add_error": "This choice may be unavailable. Please try again or choose another style.",
  "confirm_error": "Your gift could not be confirmed. Please try again.",
  "load_error": "We couldn’t load the gift choices. Please use {load} to try again.",
  "checkout_error": "We couldn’t finish checking the gift in your cart. Please try Checkout again, or remove the gift and retry.",
  "noscript": "Enable JavaScript to choose a gift and verify its discount before checkout.",
  "toggle_aria": "{action} your free {reward}",
  "remove_aria": "{action} one {product} gift",
  "option_aria": "{action} for {product}",
  "add_aria": "{action} {product} as your gift",
  "count_remaining": "{count} left to choose",
  "adding": "Adding…",
  "selected_one": "Added",
  "selected_many": "{count} added",
  "edit_cart": "Edit in cart",
  "picker_change": "Change your free {reward}",
  "select": "Select",
  "cancel": "Cancel",
  "add_another": "Add another",
  "changing": "Changing…",
  "changed": "Gift changed.",
  "change_error": "We couldn’t change your gift. Please try again."
};
const copyCache = new WeakMap();
function copyFor(host, key, values = {}) {
  let configured;
  if (host) {
    const raw = host.dataset.choiceMessages || '';
    let cached = copyCache.get(host);
    if (!cached || cached.raw !== raw) {
      let messages = {};
      try { const parsed = JSON.parse(raw); if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) messages = parsed; } catch (_) {}
      cached = { raw, messages }; copyCache.set(host, cached);
    }
    configured = cached.messages[key];
  }
  const template = typeof configured === 'string' && configured.trim() ? configured.trim() : DEFAULT_COPY[key] || '';
  return template.replace(/\{([a-z_]+)\}/g, (token, name) => Object.hasOwn(values, name) ? String(values[name]) : token);
}
function pickerCopy(key, values = {}) {
  return copyFor(document.querySelector('choice-gift-picker'), key, values);
}

const hasGiftMarker = (item) => item?.properties?._gift === 'true' || item?.properties?._gift === true;
const isMarkedGift = (item) => hasGiftMarker(item) && Boolean(item.properties._mm_choice_gift);
const currentGifts = (cart) => cart.items.filter((item) => isMarkedGift(item) && item.properties._mm_choice_gift === cart.campaign);

// Shopify Liquid property drops serialize as [key, value] pairs, while the Ajax
// cart endpoint returns an object. Normalize both before inspecting ownership.
function normalizeCart(cart) {
  if (!Array.isArray(cart?.items)) throw Error('Invalid cart items');
  return { ...cart, items: cart.items.map((item) => {
    let properties = item.properties ?? {};
    if (Array.isArray(properties)) {
      if (properties.some((pair) => !Array.isArray(pair) || pair.length !== 2 || typeof pair[0] !== 'string')) throw Error('Invalid cart properties');
      properties = Object.fromEntries(properties);
    }
    if (typeof properties !== 'object') throw Error('Invalid cart properties');
    return { ...item, properties };
  }) };
}

// final_line_price alone omits some cart-level allocations. Use Shopify's actual
// complete allocation list, and fail closed if the amounts cannot be verified.
export function payable(item) {
  if (!Array.isArray(item.discount_allocations) || typeof item.original_line_price !== 'number' || !Number.isFinite(item.original_line_price) || item.original_line_price < 0) return Infinity;
  const allocated = item.discount_allocations.reduce((sum, allocation) => sum + Number(allocation.amount), 0);
  if (!Number.isFinite(allocated)) return Infinity;
  return Math.max(0, Number(item.original_line_price) - allocated);
}

// Count actual free reward units, including gifts added through a normal PDP.
// Only picker-owned lines may be automatically removed during reconciliation.
export function selectedGiftQuantity(item, cart) {
  if (!cart?.active || !item?.eligibleReward || !Number.isSafeInteger(item.quantity) || item.quantity < 1) return 0;
  const marked = hasGiftMarker(item) || Boolean(item.properties?._mm_choice_gift);
  if (marked && (!isMarkedGift(item) || item.properties._mm_choice_gift !== cart.campaign)) return 0;
  const remaining = payable(item);
  if (remaining === 0 && (marked || item.original_line_price > 0)) return item.quantity;
  if (marked || !Number.isFinite(remaining) || !(item.original_line_price > 0)) return 0;
  const unitPrice = Number.isFinite(item.original_price) && item.original_price > 0 ? item.original_price : item.original_line_price / item.quantity;
  // A half-price line must not be mistaken for a free unit. Mixed paid/free
  // quantities need Shopify's explicit 100%-off automatic allocation evidence.
  const giftAmount = item.discount_allocations.reduce((sum, allocation) => {
    const app = allocation.discount_application;
    return app?.type === 'automatic' && app.target_type === 'line_item' && app.value_type === 'percentage' && Number(app.value) === 100
      ? sum + Math.max(0, Number(allocation.amount) || 0) : sum;
  }, 0);
  return Math.min(item.quantity, Math.floor(giftAmount / unitPrice));
}
function selectedGifts(cart) {
  return cart.items.map((item) => ({ ...item, selectedQuantity: selectedGiftQuantity(item, cart) })).filter((item) => item.selectedQuantity > 0);
}

export function removalsFor(cart) {
  const removals = [];
  const valid = [];
  for (const item of cart.items.filter((line) => hasGiftMarker(line) && !line.properties?._mm_item_gift)) {
    if (!cart.active || item.properties._mm_choice_gift !== cart.campaign || !item.eligibleReward || payable(item) !== 0) {
      removals.push({ item, quantity: 0, charged: payable(item) !== 0 });
    } else { valid.push(item); }
  }
  const alreadyFree = cart.items.filter((item) => !hasGiftMarker(item) && !item.properties?._mm_choice_gift).reduce((sum, item) => sum + selectedGiftQuantity(item, cart), 0);
  let excess = Math.max(0, valid.reduce((sum, item) => sum + item.quantity, 0) - Math.max(0, cart.earned - alreadyFree));
  // Each deliberate choice has its own private sequence, so mixed and repeated
  // choices keep their order across drawer renders, reloads and cart navigation.
  valid.sort((a, b) => Number(b.properties._mm_choice_order || 0) - Number(a.properties._mm_choice_order || 0));
  for (const item of valid) {
    if (!excess) break;
    const removed = Math.min(item.quantity, excess);
    removals.push({ item, quantity: item.quantity - removed, excess: true });
    excess -= removed;
  }
  return removals;
}

const autoMemory = new Map();
const autoKey = (cart) => cart?.token && cart?.campaign
  ? `mm-choice-auto-v1:${encodeURIComponent(cart.token)}:${encodeURIComponent(cart.campaign)}` : '';
function readAutoState(cart) {
  const key = autoKey(cart);
  let value = autoMemory.get(key);
  let persistent = Boolean(key);
  try { const stored = window.localStorage.getItem(key); if (stored) value = JSON.parse(stored); }
  catch (_) { persistent = false; }
  const valid = value?.version === 1 && Number.isSafeInteger(value.handled) && value.handled >= 0;
  return { key, persistent, existing: valid, state: valid ? { ...value, pending: Array.isArray(value.pending) ? value.pending : [] } : { version: 1, handled: 0, pending: [] } };
}
function saveAutoState(key, state) {
  if (!key) return false;
  autoMemory.set(key, state);
  try { window.localStorage.setItem(key, JSON.stringify(state)); return true; }
  catch (_) { return false; }
}

function availableAutoCandidates(items) {
  if (!Array.isArray(items)) return [];
  const seen = new Set();
  return items.filter((item) => {
    const id = Number(item?.variantId);
    if (!Number.isSafeInteger(id) || id <= 0 || !Number.isSafeInteger(Number(item?.productId))
      || Number(item.productId) <= 0 || item.available !== true || seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export class ChoiceGiftManager {
  constructor(api) {
    this.api = api;
    this.cart = null;
    this.products = [];
    this.busy = false;
    this.message = '';
    this.messageKey = '';
    this.queue = Promise.resolve();
    this.claimPending = false;
    this.pendingVariantId = null;
    this.error = false;
    this.sequence = 0;
    this.pendingAutoRemovals = [];
  }
  get selected() { return this.cart ? selectedGifts(this.cart).reduce((sum, item) => sum + item.selectedQuantity, 0) : 0; }
  emit() { this.api.onState(this); }
  setMessage(key = '') {
    this.messageKey = key;
    this.message = key ? pickerCopy(key, { load: pickerCopy('load') }) : '';
  }
  serial(task) {
    const operation = this.queue.catch(() => {}).then(() => runGiftOperation(async () => {
      this.busy = true;
      this.emit();
      try { return await task(); }
      catch (error) {
        this.error = true;
        this.setMessage('verify_error');
        throw error;
      } finally { this.busy = false; this.emit(); }
    }));
    this.queue = operation;
    return operation;
  }
  async reconcile() {
    if (this.autoRecovery) { await this.autoRecovery(); this.autoRecovery = null; }
    // Finish an interrupted replacement before ordinary excess-gift pruning.
    // Its temporary second gift must not be mistaken for a deliberate extra.
    if (this.replacementRecovery) {
      await this.replacementRecovery();
      this.replacementRecovery = null;
      this.error = false;
      this.setMessage('change_error');
    }
    await this.api.waitForCart();
    let cart = normalizeCart(await this.api.readCart());
    let changes = 0;
    while (true) {
      const removal = removalsFor(cart)[0];
      if (!removal) break;
      if (++changes > 100) throw Error('Cart is still changing');
      await this.api.waitForCart();
      await this.api.change(removal.item.key, removal.quantity);
      if (removal.charged) this.setMessage('charged_removed');
      else if (removal.excess) this.setMessage('excess_removed');
      else this.setMessage('ineligible_removed');
      cart = normalizeCart(await this.api.readCart());
    }
    this.cart = cart;
    this.flushAutoRemovals(cart);
    if (this.error) { this.error = false; this.setMessage(); }
    return cart;
  }
  refresh() {
    return this.serial(async () => {
      const previous = this.cart ? JSON.stringify(this.cart) : null;
      let cart = await this.reconcile();
      cart = await this.fulfillAuto(cart);
      if (JSON.stringify(cart) !== previous) await this.api.notify(cart);
      return cart;
    });
  }
  suppressAutoForRemoval(key, hint = {}) {
    const cart = this.cart || hint;
    const stored = readAutoState(cart);
    if (!key) return;
    const item = this.cart?.items.find((line) => line.key === key);
    const availableUnits = item ? selectedGiftQuantity(item, this.cart) : Number(hint.freeQuantity ?? hint.units);
    const units = Math.min(availableUnits, hint.units === undefined ? availableUnits : Number(hint.units));
    const quantity = item?.quantity || Number(hint.quantity);
    const variantId = Number(item?.variant_id || hint.variantId);
    if (!Number.isSafeInteger(units) || units < 1 || !Number.isSafeInteger(quantity) || quantity < 1) return;
    if (this.cart) stored.state.handled = Math.max(stored.state.handled, this.selected);
    const pending = { key, variantId, quantity, units: Math.min(units, quantity), free: availableUnits,
      properties: item?.properties || null, native: item ? !hasGiftMarker(item) && !item.properties._mm_choice_gift : hint.native === true };
    if (!stored.key) {
      const campaign = cart.campaign || hint.campaign;
      if (!campaign) return;
      this.pendingAutoRemovals = [...this.pendingAutoRemovals.filter((entry) => entry.key !== key || entry.campaign !== campaign), { ...pending, campaign }];
      return;
    }
    stored.state.pending = [...stored.state.pending.filter((entry) => entry.key !== key), pending];
    saveAutoState(stored.key, stored.state);
  }
  flushAutoRemovals(cart) {
    if (!this.pendingAutoRemovals.length || !autoKey(cart)) return;
    const stored = readAutoState(cart);
    for (const pending of this.pendingAutoRemovals.filter((entry) => entry.campaign === cart.campaign)) {
      stored.state.pending = [...stored.state.pending.filter((entry) => entry.key !== pending.key), pending];
    }
    saveAutoState(stored.key, stored.state);
    this.pendingAutoRemovals = this.pendingAutoRemovals.filter((entry) => entry.campaign !== cart.campaign);
  }
  autoState(cart) {
    const stored = readAutoState(cart);
    const selected = selectedGifts(cart).reduce((sum, item) => sum + item.selectedQuantity, 0);
    let removed = 0;
    stored.state.pending = stored.state.pending.filter((entry) => {
      if (!entry || typeof entry.key !== 'string' || !Number.isSafeInteger(entry.units) || entry.units < 1) return false;
      let item = cart.items.find((line) => line.key === entry.key);
      if (!item) {
        const matches = cart.items.filter((line) => Number(line.variant_id) === entry.variantId && (entry.properties
          ? JSON.stringify(Object.entries(line.properties).sort()) === JSON.stringify(Object.entries(entry.properties).sort())
          : entry.native && !hasGiftMarker(line) && !line.properties._mm_choice_gift));
        if (matches.length === 1) item = matches[0];
      }
      if (item && item.quantity >= entry.quantity) return true;
      // Reducing a mixed line may remove its paid unit while the free unit
      // stays. Only the actual loss of selected free units records a decline.
      removed += Math.min(entry.units, Math.max(0, (entry.free ?? entry.units) - (item ? selectedGiftQuantity(item, cart) : 0)));
      return false;
    });
    stored.state.handled = Math.min(cart.earned, Math.max(stored.state.handled, selected + removed));
    // If browser storage is blocked, do not guess whether an already-qualified
    // cart is new or was deliberately declined on a previous page. New earned
    // slots observed in this page still work using the in-memory reservation.
    if (!stored.persistent && !stored.existing) stored.state.handled = cart.earned;
    if (!saveAutoState(stored.key, stored.state) && !stored.existing) {
      stored.state.handled = cart.earned;
      saveAutoState(stored.key, stored.state);
    }
    return stored;
  }
  async fulfillAuto(initial) {
    if (!initial.active || initial.autoAdd?.enabled !== true || !autoKey(initial)) return initial;
    const run = async () => {
      let cart = await this.reconcile();
      let attempts = 0;
      while (cart.active && cart.autoAdd?.enabled === true && !this.claimPending) {
        let stored = this.autoState(cart);
        const selected = selectedGifts(cart).reduce((sum, item) => sum + item.selectedQuantity, 0);
        if (stored.state.handled >= cart.earned || selected >= cart.earned || attempts >= 100) break;
        const signature = cart.autoAdd.signature;
        const eligibility = cart.eligibilitySignature;
        const campaign = cart.campaign;
        const sameOffer = (state) => state.active && state.campaign === campaign && state.eligibilitySignature === eligibility
          && state.autoAdd?.enabled === true && state.autoAdd.signature === signature;
        let fallbackCandidates = [];
        let fallbackChecked = false;
        const discoverFallbacks = async () => {
          if (fallbackChecked) return;
          fallbackChecked = true;
          // The adapter reads the complete, freshly eligible catalog. It only
          // returns single-variant choices, so sized gifts still require a choice.
          try { fallbackCandidates = availableAutoCandidates(await this.api.getAutoCandidates?.(cart)); }
          catch (_) { fallbackCandidates = []; }
          cart = await this.reconcile();
          if (!sameOffer(cart)) fallbackCandidates = [];
        };
        const candidates = () => availableAutoCandidates([...(cart.autoAdd?.candidates || []), ...fallbackCandidates]);
        if (!candidates().length) await discoverFallbacks();
        if (!sameOffer(cart) || !candidates().length || this.selected >= cart.earned) break;
        stored = this.autoState(cart);
        if (stored.state.handled >= cart.earned) break;
        // Reserve before the first POST. Failed attempts and shopper removals
        // remain handled across reloads; a newly earned slot gets a new try.
        stored.state.handled++;
        saveAutoState(stored.key, stored.state);
        const tried = new Set();
        let fulfilled = false;
        while (attempts < 100 && !this.claimPending) {
          let candidate = candidates().find((item) => !tried.has(Number(item.variantId)));
          // Explicit default/backups take priority. Discover the remaining
          // tagged gifts only when those are unavailable or a POST loses stock.
          if (!candidate && !fallbackChecked) {
            await discoverFallbacks();
            candidate = candidates().find((item) => !tried.has(Number(item.variantId)));
          }
          if (!candidate) break;
          tried.add(Number(candidate.variantId));
          cart = await this.reconcile();
          if (!sameOffer(cart) || this.selected >= cart.earned) break;
          if (!candidates().some((item) => Number(item.variantId) === Number(candidate.variantId)
            && Number(item.productId) === Number(candidate.productId))) continue;
          attempts++;
          const id = Number(candidate.variantId);
          const order = String(Date.now() * 1000 + (++this.sequence % 1000));
          const attemptId = window.crypto?.randomUUID?.() || `${order}-${Math.random().toString(36).slice(2)}`;
          const properties = { _gift: 'true', _mm_choice_gift: campaign, _mm_choice_order: order, _mm_choice_auto: 'true', _mm_choice_auto_attempt: attemptId };
          const previous = selectedGifts(cart);
          const identity = (item) => JSON.stringify([Number(item.variant_id), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))]);
          const own = (state) => state.items.filter((item) => Number(item.variant_id) === id && item.properties._mm_choice_auto_attempt === attemptId && item.properties._mm_choice_gift === campaign);
          const read = async () => { await this.api.waitForCart(); return normalizeCart(await this.api.readCart()); };
          const clean = async () => {
            let state = await read();
            for (let retry = 0; retry < 3 && own(state).length; retry++) {
              for (const item of own(state)) {
                try { await this.api.waitForCart(); await this.api.change(item.key, 0); } catch (_) {}
                state = await read();
              }
            }
            if (own(state).length) throw Error('Automatic gift cleanup could not be verified');
            this.cart = state;
            return state;
          };
          this.pendingVariantId = id;
          this.emit();
          try {
            try { await this.api.add(id, properties); } catch (_) {}
            let verified = await read();
            const added = own(verified);
            const preserved = previous.every((old) => verified.items.filter((item) => identity(item) === identity(old))
              .reduce((sum, item) => sum + selectedGiftQuantity(item, verified), 0) >= old.selectedQuantity);
            const total = selectedGifts(verified).reduce((sum, item) => sum + item.selectedQuantity, 0);
            if (verified.active && verified.campaign === campaign && verified.eligibilitySignature === eligibility && verified.autoAdd?.enabled === true && verified.autoAdd.signature === signature
              && added.length === 1 && added[0].quantity === 1 && selectedGiftQuantity(added[0], verified) === 1 && total <= verified.earned && preserved) {
              cart = verified;
              this.cart = cart;
              this.setMessage('added');
              fulfilled = true;
              break;
            }
            this.autoRecovery = clean;
            verified = await clean();
            this.autoRecovery = null;
            cart = verified;
          } catch (error) {
            this.autoRecovery = clean;
            try { cart = await clean(); this.autoRecovery = null; }
            catch (_) { throw error; }
          } finally {
            if (this.pendingVariantId === id) this.pendingVariantId = null;
            this.emit();
          }
        }
        if (!fulfilled) {
          this.setMessage('add_error');
          if (sameOffer(cart)) {
            const failed = this.autoState(cart);
            failed.state.handled = cart.earned;
            saveAutoState(failed.key, failed.state);
          }
          cart = await this.reconcile();
          break;
        }
        cart = await this.reconcile();
      }
      this.cart = cart;
      return cart;
    };
    const locks = window.navigator?.locks;
    return locks?.request ? locks.request(autoKey(initial), run) : run();
  }
  claim(variantId) {
    // Repeated clicks during one choice are ignored, rather than queued as a
    // second intentional choice. A new click after completion can repeat it.
    if (this.claimPending) return this.queue;
    this.claimPending = true;
    this.pendingVariantId = Number(variantId);
    return this.serial(async () => {
      this.setMessage();
      const cart = await this.reconcile();
      const variant = this.products.flatMap((product) => product.variants).find((item) => Number(item.id) === Number(variantId));
      if (!variant?.available || !Number.isSafeInteger(Number(variantId)) || Number(variantId) <= 0) {
        this.setMessage('unavailable');
        await this.api.notify(cart);
        return cart;
      }
      if (!cart.active || this.selected >= cart.earned) {
        this.setMessage('no_allowance');
        await this.api.notify(cart);
        return cart;
      }
      const order = String(Date.now() * 1000 + (++this.sequence % 1000));
      const properties = { _gift: 'true', _mm_choice_gift: cart.campaign, _mm_choice_order: order };
      let addError = null;
      try { await this.api.add(Number(variantId), properties); } catch (error) { addError = error; }
      // Even a timed-out POST can have reached Shopify. Always read the cart,
      // remove unqualified/nonzero lines, then publish verified section HTML.
      const verified = await this.reconcile();
      const chosen = currentGifts(verified).find((item) => item.properties._mm_choice_order === order && Number(item.variant_id) === Number(variantId));
      if (chosen && payable(chosen) === 0) this.setMessage('added');
      else if (!this.message) this.setMessage(addError ? 'add_error' : 'confirm_error');
      if (addError && !chosen) await this.api.refreshChoices?.();
      await this.api.notify(verified);
      return verified;
    }).finally(() => { this.claimPending = false; this.pendingVariantId = null; this.emit(); });
  }
  replace(key, variantId) {
    if (this.claimPending) return this.queue;
    this.claimPending = true;
    this.pendingVariantId = Number(variantId);
    return this.serial(async () => {
      this.setMessage('changing');
      const initial = await this.reconcile();
      const original = selectedGifts(initial).find((item) => item.key === key);
      const id = Number(variantId);
      const variant = this.products.flatMap((product) => product.variants).find((item) => Number(item.id) === id);
      if (!original || !initial.active || initial.earned < 1) {
        this.setMessage('change_error');
        await this.api.notify(initial);
        return initial;
      }
      if (Number(original.variant_id) === id) {
        this.setMessage();
        return initial;
      }
      if (!variant?.available || !Number.isSafeInteger(id) || id <= 0) {
        this.setMessage('unavailable');
        await this.api.refreshChoices?.();
        await this.api.notify(initial);
        return initial;
      }

      // Discount changes can change Shopify's line key. Match a unique variant
      // and exact properties, never a different customer's paid line by ID alone.
      const identity = (item) => JSON.stringify([Number(item.variant_id), Object.entries(item.properties || {}).sort(([a], [b]) => a.localeCompare(b))]);
      const originalIdentity = identity(original);
      const originals = (cart) => cart.items.filter((item) => identity(item) === originalIdentity);
      if (originals(initial).length !== 1) {
        this.setMessage('change_error');
        await this.api.notify(initial);
        return initial;
      }
      const originalQuantity = original.quantity;
      const paidQuantity = originalQuantity - original.selectedQuantity;
      const sameOffer = (cart) => cart.active && cart.campaign === initial.campaign
        && cart.eligibilitySignature === initial.eligibilitySignature && cart.earned >= initial.earned;
      const order = String(Date.now() * 1000 + (++this.sequence % 1000));
      const properties = { _gift: 'true', _mm_choice_gift: initial.campaign, _mm_choice_order: order };
      const addedLines = (cart) => cart.items.filter((item) => Number(item.variant_id) === id
        && item.properties._mm_choice_gift === initial.campaign && item.properties._mm_choice_order === order);
      const read = async () => {
        let lastError;
        for (let attempt = 0; attempt < 2; attempt++) {
          try { await this.api.waitForCart(); return normalizeCart(await this.api.readCart()); }
          catch (error) { lastError = error; }
        }
        throw lastError;
      };
      let removalAttempted = false;
      let restorationAttempted = false;
      const changeAndRead = async (lineKey, quantity, onAttempt) => {
        // A rejected/timed-out response can still mean Shopify saved the write.
        try { await this.api.waitForCart(); onAttempt?.(); await this.api.change(lineKey, quantity); } catch (_) {}
        return read();
      };
      const recover = async () => {
        let cart = await read();
        for (let attempt = 0; attempt < 3 && addedLines(cart).length; attempt++) {
          for (const item of addedLines(cart)) cart = await changeAndRead(item.key, 0);
        }
        if (addedLines(cart).length) throw Error('Replacement could not be removed');
        const lines = originals(cart);
        if (lines.length > 1) throw Error('Original gift line is ambiguous');
        const quantity = lines[0]?.quantity || 0;
        // Only compensate our one-unit decrement. External quantity changes
        // must not be overwritten, and paid units on a mixed line stay intact.
        if (removalAttempted && quantity === originalQuantity - 1 && sameOffer(cart)) {
          if (!restorationAttempted) {
            restorationAttempted = true;
            try { await this.api.add(Number(original.variant_id), { ...original.properties }); } catch (_) {}
            cart = await read();
          }
        }
        if (restorationAttempted) {
          const restored = originals(cart);
          if (restored.length !== 1 || restored[0].quantity !== originalQuantity) {
            // A later user retry may restore an unavailable/failed addition,
            // but only after this fresh read proves the unit was not added.
            if (restored.reduce((sum, item) => sum + item.quantity, 0) === originalQuantity - 1) restorationAttempted = false;
            throw Error('Original gift could not be restored');
          }
          if (selectedGiftQuantity(restored[0], cart) < original.selectedQuantity) {
            cart = await changeAndRead(restored[0].key, originalQuantity - 1);
            if (originals(cart).some((item) => item.quantity > originalQuantity - 1)) throw Error('Restored gift is not free');
            restorationAttempted = false;
          }
        }
        if (originals(cart).reduce((sum, item) => sum + item.quantity, 0) < paidQuantity) throw Error('Paid quantity changed during replacement');
        this.cart = cart;
        return cart;
      };

      let result;
      let changed = false;
      try {
        // Keep the selected gift until the uniquely marked new unit is visible.
        // Do not reconcile this overlap: Shopify may temporarily charge either.
        try { await this.api.add(id, properties); } catch (_) {}
        let cart = await read();
        const added = addedLines(cart);
        const oldLines = originals(cart);
        if (!sameOffer(cart) || added.length !== 1 || added[0].quantity !== 1 || !added[0].eligibleReward
          || oldLines.length !== 1 || oldLines[0].quantity !== originalQuantity || !oldLines[0].eligibleReward) {
          throw Error('Replacement was not confirmed');
        }
        cart = await changeAndRead(oldLines[0].key, originalQuantity - 1, () => { removalAttempted = true; });
        const chosen = addedLines(cart);
        const oldQuantity = originals(cart).reduce((sum, item) => sum + item.quantity, 0);
        const selected = selectedGifts(cart).reduce((sum, item) => sum + item.selectedQuantity, 0);
        const previousSelectionsPreserved = selectedGifts(initial).every((previous) => {
          const required = previous.selectedQuantity - (previous.key === key ? 1 : 0);
          return cart.items.filter((item) => identity(item) === identity(previous))
            .reduce((sum, item) => sum + selectedGiftQuantity(item, cart), 0) >= required;
        });
        if (!sameOffer(cart) || chosen.length !== 1 || chosen[0].quantity !== 1 || selectedGiftQuantity(chosen[0], cart) !== 1
          || oldQuantity !== originalQuantity - 1 || oldQuantity < paidQuantity || selected > cart.earned || !previousSelectionsPreserved) {
          throw Error('Replacement gift is not free');
        }
        result = cart;
        changed = true;
      } catch (_) {
        // Keep a recovery operation if the network is unavailable. Refresh and
        // checkout must finish it before normal reconciliation can continue.
        this.replacementRecovery = recover;
        result = await recover();
        this.replacementRecovery = null;
      }
      this.cart = result;
      this.error = false;
      this.setMessage(changed ? 'changed' : 'change_error');
      if (!changed) await this.api.refreshChoices?.().catch(() => {});
      await this.api.notify(result);
      return result;
    }).finally(() => { this.claimPending = false; this.pendingVariantId = null; this.emit(); });
  }
  remove(key) {
    return this.serial(async () => {
      const cart = await this.reconcile();
      const item = selectedGifts(cart).find((line) => line.key === key);
      if (item) this.suppressAutoForRemoval(key);
      if (item && item.selectedQuantity === item.quantity) await this.api.change(item.key, Math.max(0, item.quantity - 1));
      const verified = await this.reconcile();
      this.setMessage('removed');
      await this.api.notify(verified);
      return verified;
    });
  }
}

const route = (path) => `${window.Shopify?.routes?.root || '/'}${path}`;
const pause = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
function sectionIds() {
  return [...new Set(Array.from(document.querySelectorAll('cart-items-component[data-section-id]'), (element) => element.dataset.sectionId))].slice(0, 5);
}
async function request(url, options = {}, type = 'json') {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15000);
  try {
    const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', ...options, signal: controller.signal });
    const data = type === 'text' ? await response.text() : await response.json();
    if (!response.ok || data?.status >= 400 || data?.errors) throw Error('Cart request failed');
    return data;
  } finally { window.clearTimeout(timeout); }
}
function post(path, body) {
  return request(route(path), { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) });
}
async function waitForCart() {
  const deadline = Date.now() + 8000;
  while (document.querySelector('cart-items-component.cart-items-disabled')) {
    if (Date.now() > deadline) throw Error('Cart update is still pending');
    await pause(80);
  }
}
async function notifyCart(cart) {
  const entries = await Promise.all(sectionIds().map(async (id) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.delete('view');
    url.searchParams.set('section_id', id);
    return [id, await request(url.toString(), { headers: { Accept: 'text/html' } }, 'text')];
  }));
  document.dispatchEvent(new CartUpdateEvent(cart, SOURCE, { source: SOURCE, itemCount: cart.item_count, sections: Object.fromEntries(entries) }));
}

let manager;
let productsPromise;
let loadedChoices = false;
let nextPage = null;
let catalogState = 'unknown';
let catalogSignature = '';
let pendingCatalogSignature = '';
let catalogCheckedAt = 0;
let refreshChoicesQueued = false;
const CHOICE_FRESHNESS_MS = 60000;
const openPickers = new Set();
const variantSelections = new Map();
const pickerObservers = new WeakMap();
const headerObservers = new WeakMap();
const pickerObservation = { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: ['hidden', 'aria-expanded', 'data-choice-messages'] };
const headerObservation = { ...pickerObservation, attributeFilter: [...pickerObservation.attributeFilter, 'data-eligibility-signature', 'data-active', 'data-campaign', 'data-buy-quantity', 'data-get-quantity', 'data-max-quantity', 'data-trigger-name', 'data-trigger-name-plural', 'data-reward-name', 'data-reward-name-plural', 'data-choice-shop-url', 'data-headline'] };
let checkoutPending = false;
let allowCheckout = false;

function safeImage(value) {
  if (!value) return null;
  try { const url = new URL(value, window.location.origin); return ['https:', 'http:'].includes(url.protocol) ? url.href : null; } catch (_) { return null; }
}
function element(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}
function viewKey(node) { return node.nodeType === 1 ? node.getAttribute('data-choice-card') || node.getAttribute('data-choice-selected-key') : null; }
function syncViewChildren(parent, incoming) {
  const previous = Array.from(parent.childNodes);
  const used = new Set();
  incoming.forEach((next, index) => {
    const key = viewKey(next);
    let node = key ? previous.find((candidate) => viewKey(candidate) === key) : previous[index];
    if (!node || used.has(node) || viewKey(node) !== key || node.nodeType !== next.nodeType || node.nodeName !== next.nodeName) node = next;
    else if (node.nodeType === 3) { if (node.textContent !== next.textContent) node.textContent = next.textContent; }
    else if (node.nodeType === 1) {
      const selectedValue = next.tagName === 'SELECT' ? next.value : null;
      for (const attribute of Array.from(node.attributes)) if (!next.hasAttribute(attribute.name)) node.removeAttribute(attribute.name);
      for (const attribute of Array.from(next.attributes)) if (node.getAttribute(attribute.name) !== attribute.value) node.setAttribute(attribute.name, attribute.value);
      syncViewChildren(node, Array.from(next.childNodes));
      if (selectedValue !== null && node.value !== selectedValue) node.value = selectedValue;
    }
    used.add(node);
    if (parent.childNodes[index] !== node) parent.insertBefore(node, parent.childNodes[index] || null);
  });
  for (const node of previous) if (!used.has(node) && node.parentNode === parent) node.remove();
}
function giftNames(picker) {
  const reward = picker.dataset.rewardName || 'gift';
  const trigger = picker.dataset.triggerName || 'item';
  return {
    reward, trigger,
    rewards: picker.dataset.rewardNamePlural || `${reward}s`,
    triggers: picker.dataset.triggerNamePlural || `${trigger}s`,
  };
}
function qualifyingShopURL(picker) {
  if (!picker.dataset.choiceShopUrl?.trim()) return null;
  try {
    const url = new URL(picker.dataset.choiceShopUrl, window.location.origin);
    return url.origin === window.location.origin && ['https:', 'http:'].includes(url.protocol) ? url : null;
  } catch (_) { return null; }
}
export function choiceHeaderState(cart, config) {
  if (!cart || !Array.isArray(cart.items) || !Number.isInteger(cart.qualifyingQuantity) || cart.qualifyingQuantity < 0 || !Number.isInteger(cart.earned) || cart.earned < 0) return null;
  if (typeof config.eligibilitySignature !== 'string' || !config.eligibilitySignature || cart.eligibilitySignature !== config.eligibilitySignature) return null;
  if (config.campaign && cart.campaign !== config.campaign) return null;
  const buy = Math.max(1, Math.floor(Number(config.buyQuantity) || 1));
  const get = Math.max(1, Math.floor(Number(config.getQuantity) || 1));
  const max = Math.max(0, Math.floor(Number(config.maxQuantity) || 0));
  const units = cart.qualifyingQuantity;
  const earned = Math.min(Math.floor(units / buy) * get, max || Infinity);
  // An editor section may have newer rules than the last verified cart read.
  // Keep its fresh Liquid state instead of painting old campaign data over it.
  if (earned !== cart.earned) return null;
  const selected = Math.min(earned, selectedGifts(cart).reduce((sum, item) => sum + item.selectedQuantity, 0));
  const capped = max > 0 && earned >= max;
  const nextUnits = buy - (units % buy);
  const nextAward = capped ? 0 : Math.min(get, max > 0 ? max - earned : Infinity);
  return { active: cart.active, units, earned, selected, capped, nextUnits, nextAward, complete: earned > 0 && selected >= earned, progress: capped ? 100 : (units % buy) / buy * 100, showCue: !capped && (earned > 0 || buy > 1 || get > 1) };
}
function renderHeader(header) {
  const observer = headerObservers.get(header);
  observer?.disconnect();
  try { renderHeaderContents(header); }
  finally { if (header.isConnected) observer?.observe(header, headerObservation); }
}
function renderHeaderContents(header) {
  if (!manager) return;
  const required = ['headline', 'text', 'link', 'current', 'next', 'fill'];
  if (required.some((name) => !header.querySelector(`[data-choice-header-${name}]`))) return;
  header.setAttribute('aria-busy', String(manager.busy));
  if (manager.busy || !manager.cart) return;
  const state = choiceHeaderState(manager.cart, header.dataset);
  if (!state) return;
  header.hidden = !state.active;
  if (!state.active) return;
  const names = giftNames(header);
  const neutral = manager.error;
  const earned = neutral ? 0 : state.earned;
  header.classList.toggle('is-earned', earned > 0);
  header.classList.toggle('is-complete', !neutral && state.capped);
  const unclaimed = Math.max(0, state.earned - state.selected);
  const nextPurchase = copyFor(header, 'header_next', {
    needed: state.nextUnits, more: state.units > 0 ? ' more' : '',
    trigger: state.nextUnits === 1 ? names.trigger : names.triggers,
    award: state.nextAward === 1 ? (state.earned > 0 ? 'another' : 'a') : `${state.nextAward}${state.earned > 0 ? ' more' : ''}`,
    reward: state.nextAward === 1 ? names.reward : names.rewards,
  });
  let message = header.dataset.headline || `Free ${names.reward} with every ${names.trigger}`;
  if (!neutral && unclaimed > 0) message = copyFor(header, unclaimed === 1 ? 'header_select_one' : 'header_select_many', { count: unclaimed, reward: unclaimed === 1 ? names.reward : names.rewards });
  else if (!neutral && state.showCue) message = nextPurchase;
  else if (!neutral && state.complete) message = copyFor(header, 'header_added', { count: earned, reward: earned === 1 ? names.reward : names.rewards });
  const text = header.querySelector('[data-choice-header-text]');
  const link = header.querySelector('[data-choice-header-link]');
  const shopURL = qualifyingShopURL(header);
  const showLink = Boolean(shopURL && (neutral || (unclaimed === 0 && !state.capped)));
  text.textContent = message;
  link.textContent = message;
  text.hidden = showLink;
  link.hidden = !showLink;
  if (shopURL) link.href = shopURL.toString();
  header.querySelector('[data-choice-header-current]').textContent = copyFor(header, 'header_unlocked', { count: state.earned });
  header.querySelector('[data-choice-header-next]').textContent = state.capped ? copyFor(header, 'header_complete') : copyFor(header, 'header_reward', { count: state.nextAward, reward: state.nextAward === 1 ? names.reward : names.rewards });
  header.querySelector('[data-choice-header-fill]').style.width = `${state.progress}%`;
  const steps = header.querySelector('.choice-gift-header__steps');
  if (steps) steps.hidden = neutral;
}
function renderPicker(picker) {
  // Horizon may keep this custom-element host while morphing its descendants.
  // Observe those external changes, but never observe our own render mutations.
  const observer = pickerObservers.get(picker);
  observer?.disconnect();
  try { renderPickerContents(picker); }
  finally { if (picker.isConnected) observer?.observe(picker, pickerObservation); }
}
function giftPhoto(url) {
  const image = safeImage(url);
  if (!image) { const placeholder = element('span', 'choice-gift__image-placeholder'); placeholder.setAttribute('aria-hidden', 'true'); return placeholder; }
  const img = element('img'); img.src = image; img.alt = ''; img.loading = 'lazy'; img.width = 64; img.height = 64;
  return img;
}
function lineToggle(picker) {
  return [...document.querySelectorAll('[data-choice-line-toggle]')].find((node) => node.dataset.choiceLineToggle === picker.dataset.pickerId);
}
function lineTarget(picker) {
  if (picker.dataset.choiceContext !== 'line' || !manager.cart) return null;
  // Never choose a different line merely because it has the same product ID.
  return selectedGifts(manager.cart).find((item) => item.key === picker.dataset.lineKey) || null;
}
function renderPickerContents(picker) {
  const context = picker.dataset.choiceContext;
  const browsing = context === 'controller';
  if ((!browsing && !['line', 'claim'].includes(context)) || (browsing && !openPickers.has(picker.dataset.pickerId))) { picker.hidden = true; return; }
  const required = ['panel', 'status', 'selected', 'products', 'more'];
  if (required.some((name) => !picker.querySelector('[data-choice-' + name + ']'))) return;
  const toggle = lineToggle(picker);
  const control = toggle?.closest('[data-choice-line-control]');
  const cell = picker.closest('.choice-gift__line-cell');
  const id = picker.dataset.pickerId;
  const active = Boolean(manager.cart?.active);
  const target = lineTarget(picker);
  const alternatives = manager.products.some((product) => product.variants.some((variant) => variant.available === true && Number(variant.id) !== Number(target?.variant_id)));
  const unclaimed = Math.max(0, (manager.cart?.earned || 0) - manager.selected);
  // The browse-only preview belongs to an unqualified cart. Once a gift is
  // earned, its inline claim/change controls take over instead of leaving the
  // old preview above newly added merchandise.
  const canOpen = active && (browsing ? manager.cart.earned === 0 && manager.selected === 0
    : context === 'line' ? Boolean(target && (alternatives || nextPage)) : unclaimed > 0 && catalogState !== 'exhausted');
  if (!canOpen) openPickers.delete(id);
  const expanded = canOpen && openPickers.has(id);
  const blocked = manager.busy || manager.claimPending || Boolean(productsPromise);
  const names = giftNames(picker);
  const copy = (key, values = {}) => copyFor(picker, key, { reward: names.reward, ...values });
  if (control) control.hidden = !canOpen;
  if (toggle) {
    toggle.textContent = context === 'line' ? copy('change') : copy('picker_title', { reward: unclaimed > 1 ? names.rewards : names.reward });
    toggle.setAttribute('aria-expanded', String(Boolean(expanded)));
    toggle.disabled = Boolean(blocked);
  }
  picker.hidden = !expanded;
  if (cell) cell.hidden = !expanded;
  picker.querySelector('[data-choice-panel]').hidden = !expanded;
  picker.setAttribute('aria-busy', String(Boolean(blocked)));
  if (!expanded) return;
  const status = picker.querySelector('[data-choice-status]');
  const statusKey = manager.messageKey || (manager.message ? '' : productsPromise ? 'loading' : manager.busy ? 'checking' : '');
  status.textContent = statusKey ? copy(statusKey, { load: copy('load') }) : manager.message;
  status.classList.toggle('choice-gift__status--quiet', !manager.error && ['added', 'changed', 'removed', 'checking', 'loading'].includes(statusKey));
  const retry = picker.querySelector('[data-choice-retry]');
  if (retry) { retry.hidden = !manager.error; retry.disabled = blocked; retry.textContent = copy('retry'); }
  const close = picker.querySelector('[data-choice-close]');
  if (close) { close.textContent = copy('close'); close.disabled = blocked; }
  const note = picker.querySelector('[data-choice-note]');
  if (note) { note.hidden = !(target?.selectedQuantity > 1); note.textContent = note.hidden ? '' : 'Changes one gift at a time.'; }
  const help = picker.querySelector('[data-choice-help]');
  if (help) {
    help.replaceChildren(); help.hidden = true;
    if (browsing) {
      const url = qualifyingShopURL(picker);
      if (url) { const link = element('a', 'choice-gift__shop', copy('shop', { trigger: names.triggers })); link.href = url.toString(); help.append(link); help.hidden = false; }
    }
  }

  // The selected gift appears once, first, with its own image. Other selected
  // cart gifts remain ordinary choices so one line can be changed independently.
  const selectedRows = [];
  if (target) {
    const card = element('div', 'choice-gift__product is-selected');
    card.dataset.choiceSelectedKey = target.key;
    const product = manager.products.find((item) => Number(item.id) === Number(target.product_id));
    card.append(giftPhoto(target.image || product?.image));
    const info = element('div', 'choice-gift__product-info');
    info.append(element('h4', '', target.title));
    if (product?.variants.length > 1 && target.variant_title) info.append(element('small', 'choice-gift__variant', target.variant_title));
    const selected = element('button', 'choice-gift__add choice-gift__current', copy('selected_one'));
    selected.type = 'button'; selected.disabled = true; selected.dataset.current = 'true';
    selected.setAttribute('aria-label', target.title + ', selected gift');
    card.append(info, selected); selectedRows.push(card);
  }
  syncViewChildren(picker.querySelector('[data-choice-selected]'), selectedRows);
  const productCards = [];
  for (const product of manager.products) {
    const variants = product.variants.filter((variant) => Number(variant.id) !== Number(target?.variant_id));
    if (!variants.some((variant) => variant.available === true)) continue;
    const card = element('div', 'choice-gift__product');
    card.dataset.choiceCard = String(product.id);
    card.append(giftPhoto(product.image));
    const info = element('div', 'choice-gift__product-info');
    info.append(element('h4', '', product.title));
    const label = element('label', '', copy('option'));
    label.hidden = browsing || product.variants.length <= 1;
    const select = element('select'); select.dataset.choiceProduct = String(product.id);
    select.setAttribute('aria-label', copy('option_aria', { action: copy('option'), product: product.title }));
    if (product.variants.length > 1) {
      const placeholder = element('option', '', copy('select') + ' ' + copy('option').toLowerCase()); placeholder.value = ''; placeholder.disabled = true; select.append(placeholder);
    }
    for (const variant of variants) {
      const option = element('option', '', variant.title + (variant.available === true ? '' : ' — ' + copy('sold_out')));
      option.value = String(variant.id); option.disabled = variant.available !== true; select.append(option);
    }
    const saved = variantSelections.get(String(product.id));
    const variant = product.variants.length <= 1 ? variants.find((item) => item.available === true)
      : variants.find((item) => String(item.id) === saved && item.available === true);
    select.value = variant ? String(variant.id) : '';
    select.disabled = Boolean(blocked);
    label.append(select); info.append(label); card.append(info);
    const adding = Boolean(manager.pendingVariantId && variants.some((item) => Number(item.id) === manager.pendingVariantId));
    card.classList.toggle('is-adding', adding);
    const action = copy(adding ? target ? 'changing' : 'adding' : 'select');
    const button = element('button', 'choice-gift__add', action);
    if (adding) button.setAttribute('aria-busy', 'true');
    button.type = 'button'; button.hidden = browsing;
    if (target) { button.dataset.choiceReplace = String(variant?.id || ''); button.dataset.choiceFrom = target.key; }
    else button.dataset.choiceAdd = String(product.id);
    button.disabled = Boolean(blocked || catalogState !== 'available' || !variant || !active || (!target && unclaimed < 1));
    button.setAttribute('aria-label', copy('add_aria', { action, product: product.title }));
    card.append(button); productCards.push(card);
  }
  if (loadedChoices && !manager.products.length) productCards.push(element('p', 'choice-gift__hint', copy(nextPage ? 'empty_page' : 'empty_catalog')));
  syncViewChildren(picker.querySelector('[data-choice-products]'), productCards);
  const more = picker.querySelector('[data-choice-more]');
  more.hidden = loadedChoices && !nextPage;
  more.disabled = blocked;
  more.textContent = copy(loadedChoices ? 'more' : 'load');
}
function renderProductActions() {
  if (!manager?.cart) return;
  document.querySelectorAll('[data-choice-gift-product][data-choice-role="reward"]').forEach((container) => {
    const previous = container.querySelector('.choice-gift-product__action');
    if (!previous) return;
    const preferred = container.dataset.choiceVariant;
    const knownProduct = manager.products.find((product) => product.variants.some((variant) => String(variant.id) === preferred));
    const knownVariant = knownProduct?.variants.find((variant) => String(variant.id) === preferred && variant.available);
    // An unlisted product is only known unavailable after every page is checked.
    container.hidden = loadedChoices && !nextPage && !knownProduct;
    const canClaim = catalogState === 'available' && manager.cart.active && manager.cart.earned > manager.selected;
    const shopURL = qualifyingShopURL(container);
    if (!canClaim && !shopURL) { previous.hidden = true; return; }
    const tag = canClaim ? 'BUTTON' : 'A';
    const action = previous.tagName === tag ? previous : element(tag.toLowerCase(), previous.className);
    action.hidden = false;
    if (canClaim) {
      action.type = 'button'; action.dataset.choiceGiftOpen = '';
      action.disabled = manager.busy || Boolean(productsPromise);
      if (knownVariant) action.dataset.choiceGiftVariant = preferred;
      else delete action.dataset.choiceGiftVariant;
      action.textContent = copyFor(container, knownVariant ? 'choose_this' : 'choose_a', { reward: container.dataset.choiceRewardName || 'gift' });
    } else {
      action.href = shopURL.toString();
      action.textContent = copyFor(container, 'shop', { trigger: container.dataset.choiceTriggerNamePlural || `${container.dataset.choiceTriggerName || 'item'}s` });
    }
    if (action !== previous) { const hadFocus = document.activeElement === previous; previous.replaceWith(action); if (hadFocus) action.focus(); }
  });
}
function renderAll() { document.querySelectorAll('choice-gift-picker').forEach(renderPicker); document.querySelectorAll('choice-gift-header').forEach(renderHeader); renderProductActions(); }

function searchURL(next) {
  const tag = manager?.cart?.rewardTag ?? document.querySelector('choice-gift-picker')?.dataset.rewardTag ?? '';
  const url = new URL(next || route('search'), window.location.origin);
  if (url.origin !== window.location.origin || url.pathname !== new URL(route('search'), window.location.origin).pathname) throw Error('Invalid choice page');
  url.searchParams.set('q', `tag:"${tag.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
  url.searchParams.set('view', 'mm-choice-gifts'); url.searchParams.set('type', 'product');
  const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
  if (preview) url.searchParams.set('preview_theme_id', preview);
  return url;
}
function choiceCatalogKey() {
  return `${manager?.cart?.eligibilitySignature || ''}:${manager?.cart?.rewardTag ?? document.querySelector('choice-gift-picker')?.dataset.rewardTag ?? ''}`;
}
function setCatalogState(value) {
  catalogState = value;
  document.documentElement.dataset.choiceStock = value;
}
async function loadChoices(more = false, force = false, queueIfPending = false) {
  if (productsPromise) {
    if (queueIfPending || choiceCatalogKey() !== pendingCatalogSignature) refreshChoicesQueued = true;
    return productsPromise;
  }
  const signature = choiceCatalogKey();
  const reset = force || signature !== catalogSignature || Date.now() - catalogCheckedAt >= CHOICE_FRESHNESS_MS;
  if (loadedChoices && !more && !reset) return;
  productsPromise = (async () => {
    let fresh = reset || !loadedChoices;
    do {
      refreshChoicesQueued = false;
      const expected = choiceCatalogKey();
      pendingCatalogSignature = expected;
      if (fresh && (expected !== catalogSignature || !loadedChoices || catalogState !== 'available')) {
        manager.products = []; nextPage = null; loadedChoices = false;
        setCatalogState('unknown');
      }
      try {
        const all = new Map((fresh ? [] : manager.products).map((product) => [String(product.id), product]));
        let next = more && !fresh ? nextPage : null;
        const visited = new Set();
        // Stop as soon as an available choice is found. Only declare exhaustion
        // after the entire result set is checked; a failed/truncated page is unknown.
        do {
          const url = searchURL(next).toString();
          if (visited.has(url) || visited.size >= 20) throw Error('Incomplete gift choices');
          visited.add(url);
          const data = await request(url);
          if (!Array.isArray(data.products)) throw Error('Invalid choices');
          if (expected !== choiceCatalogKey() || (!data.eligibilitySignature || data.eligibilitySignature !== manager.cart?.eligibilitySignature)) throw Error('Gift settings changed');
          for (const product of data.products) {
            if (!Array.isArray(product.variants)) throw Error('Invalid choice variants');
            if (product.variants.some((variant) => variant.available)) all.set(String(product.id), product);
            else all.delete(String(product.id));
          }
          next = data.nextPage || null;
        } while (!all.size && next);
        manager.products = [...all.values()]; nextPage = next; loadedChoices = true;
        catalogSignature = expected; catalogCheckedAt = Date.now();
        setCatalogState(manager.products.length ? 'available' : 'exhausted');
        if (manager.messageKey === 'load_error') manager.setMessage();
      } catch (_) {
        loadedChoices = false;
        setCatalogState('unknown');
        manager.setMessage('load_error');
      }
      fresh = true;
    } while (refreshChoicesQueued);
  })().finally(() => {
    productsPromise = null; renderAll();
  });
  renderAll();
  return productsPromise;
}
async function getAutoCandidates(cart) {
  const expected = cart.eligibilitySignature;
  const campaign = cart.campaign;
  if (!expected || !campaign) return [];
  await loadChoices(false, true);
  const pages = new Set();
  while (loadedChoices && nextPage && catalogState === 'available') {
    if (pages.has(nextPage) || pages.size >= 20) return [];
    pages.add(nextPage);
    await loadChoices(true);
  }
  if (!loadedChoices || nextPage || catalogState !== 'available' || manager.cart?.eligibilitySignature !== expected || manager.cart?.campaign !== campaign || !manager.cart.active) return [];
  return manager.products.filter((product) => product.variants.length === 1 && product.variants[0].available === true)
    .map((product) => ({ productId: Number(product.id), variantId: Number(product.variants[0].id), available: true }));
}
function findPicker(id) { return [...document.querySelectorAll('choice-gift-picker')].find((item) => item.dataset.pickerId === id); }
function focusPicker(id, variantId) {
  let picker = findPicker(id);
  if (!picker && variantId) picker = [...document.querySelectorAll('choice-gift-picker[data-choice-context="line"]')].find((item) => Number(item.dataset.lineVariantId) === Number(variantId));
  const toggle = picker && lineToggle(picker);
  if (toggle && !toggle.closest('[hidden]')) toggle.focus({ preventScroll: true });
  else {
    // A single-choice replacement may have no change link. Return focus to its
    // cart row instead of letting it fall back to the page behind the drawer.
    const row = picker?.closest('[data-key]');
    const fallback = row?.querySelector('.cart-items__remove') || picker?.closest('dialog')?.querySelector('button');
    fallback?.focus({ preventScroll: true });
  }
}
function navigateToCart(url) { window.location.assign(url); }
async function openLinePicker(picker) {
  const id = picker.dataset.pickerId;
  if (openPickers.has(id)) { openPickers.delete(id); renderAll(); focusPicker(id); return; }
  openPickers.clear(); openPickers.add(id); renderAll();
  await manager.refresh().catch(() => {});
  await loadChoices(false, true);
  renderAll();
  const current = findPicker(id);
  if (current && !current.hidden) current.querySelector('[data-choice-close]')?.focus({ preventScroll: true });
}
async function openPicker(button) {
  const drawer = document.querySelector('cart-drawer-component');
  drawer?.open?.();
  await manager.refresh().catch(() => {});
  await loadChoices(false, true);
  const available = [...(drawer || document).querySelectorAll('choice-gift-picker[data-choice-context="claim"],choice-gift-picker[data-choice-context="line"]')];
  const picker = available.find((item) => { const toggle = lineToggle(item); return toggle && !toggle.closest('[hidden]'); })
    || (drawer || document).querySelector('choice-gift-picker[data-choice-context="controller"]');
  if (!picker) {
    // Without a rendered cart surface, preserve intent across cart navigation.
    if (!drawer && !document.querySelector('cart-items-component')) {
      const url = new URL(route('cart'), window.location.origin); url.searchParams.set('choice_gift_open', '1');
      if (button?.dataset.choiceGiftVariant) url.searchParams.set('choice_gift_variant', button.dataset.choiceGiftVariant);
      navigateToCart(url.toString());
    }
    return;
  }
  const preferred = button?.dataset.choiceGiftVariant;
  const product = preferred && manager.products.find((item) => item.variants.some((variant) => String(variant.id) === preferred && variant.available === true));
  if (product) { variantSelections.set(String(product.id), preferred); manager.products = [product, ...manager.products.filter((item) => item !== product)]; }
  openPickers.clear(); openPickers.add(picker.dataset.pickerId); renderAll();
  picker.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
  picker.querySelector('[data-choice-close]')?.focus({ preventScroll: true });
}
function checkoutError(message) {
  for (const container of document.querySelectorAll('.cart__ctas')) {
    let error = container.querySelector('.choice-gift__checkout-error');
    if (!error) { error = element('p', 'choice-gift__checkout-error'); error.setAttribute('role', 'alert'); container.prepend(error); }
    error.textContent = message;
  }
}
async function prepareCheckout(button, form) {
  if (checkoutPending) return;
  checkoutPending = true; button?.setAttribute('aria-busy', 'true');
  try {
    await verifyItemGiftsForCheckout();
    try {
      await manager.refresh();
      // A separate final read catches a cart update during section rendering.
      await manager.serial(async () => manager.fulfillAuto(await manager.reconcile()));
    } catch (error) {
      // An unavailable custom view must not strand an ordinary paid cart. The
      // native endpoint can prove that no gift needs eligibility verification;
      // any promotional marker still requires the full check to succeed.
      await waitForCart();
      const cart = normalizeCart(await request(route('cart.js')));
      if (!Array.isArray(cart.items) || cart.items.some((item) => hasGiftMarker(item) || item.properties?._mm_choice_gift)) throw error;
    }
    await verifyItemGiftsForCheckout();
    const currentForm = document.getElementById(form?.id || 'cart-form') || form;
    const submitter = currentForm?.querySelector('[name="checkout"]') || document.querySelector('button[name="checkout"]');
    allowCheckout = true;
    try { if (currentForm && submitter) currentForm.requestSubmit(submitter); else window.location.assign(route('checkout')); }
    finally { allowCheckout = false; }
  } catch (_) { checkoutError(pickerCopy('checkout_error')); }
  finally { checkoutPending = false; document.querySelectorAll('[name="checkout"]').forEach((node) => node.removeAttribute('aria-busy')); }
}
function interceptCheckout(event) {
  if (allowCheckout) return;
  const button = event.type === 'submit' ? event.submitter : event.target.closest?.('[name="checkout"], a[href$="/checkout"]');
  const form = event.type === 'submit' ? event.target : button?.form;
  if (event.type === 'submit' ? (button ? button.name !== 'checkout' : form?.id !== 'cart-form') : !button) return;
  event.preventDefault(); event.stopImmediatePropagation(); prepareCheckout(button, form);
}
function captureGiftRemoval(event) {
  const control = event.type === 'click'
    ? event.target.closest?.('.cart-items__remove, [data-action="decrease"], .quantity-minus')
    : event.target.matches?.('input[name="updates[]"]') ? event.target : null;
  if (!control || control.disabled) return;
  const row = control.closest('[data-key][data-quantity]');
  if (!row || row.dataset.itemGift) return;
  const quantity = Number(row.dataset.quantity);
  const removingLine = control.matches('.cart-items__remove');
  const decrease = removingLine ? quantity : event.type === 'click' ? 1 : quantity - Number(control.value);
  if (!(decrease > 0)) return;
  const item = manager.cart?.items.find((line) => line.key === row.dataset.key);
  const knownFree = item && selectedGiftQuantity(item, manager.cart);
  // The server-rendered row is available before the first cart request. A
  // marked or fully-free line provides a conservative initial removal intent.
  const renderedFree = Number(row.dataset.choiceFreeQuantity);
  const initialFree = Number.isSafeInteger(renderedFree) && renderedFree >= 0 ? renderedFree
    : row.dataset.isGift === 'true' || Number(row.dataset.finalLinePrice) === 0 ? quantity : 0;
  const free = item ? knownFree : initialFree;
  if (!free) return;
  const picker = document.querySelector('choice-gift-picker[data-campaign]');
  manager.suppressAutoForRemoval(row.dataset.key, {
    token: picker?.dataset.cartToken,
    campaign: picker?.dataset.campaign,
    variantId: Number(row.dataset.variantId),
    quantity,
    units: Math.min(free, decrease),
    freeQuantity: free,
    native: row.dataset.isGift !== 'true',
  });
}
export function initChoiceGiftPicker() {
  if (window.__mmChoiceGiftPicker) return;
  window.__mmChoiceGiftPicker = true;
  registerPrimaryGiftGuard();
  manager = new ChoiceGiftManager({
    readCart: async () => {
      const url = new URL(route('cart?view=mm-choice-gifts'), window.location.origin);
      const preview = new URL(window.location.href).searchParams.get('preview_theme_id');
      if (preview) url.searchParams.set('preview_theme_id', preview);
      // Liquid does not expose cart.token. Read native cart identity fresh for
      // every eligibility snapshot; never use native data for promotion rules.
      const [cart, native] = await Promise.all([
        request(url.toString()),
        request(route('cart.js')).catch(() => null),
      ]);
      if (!Array.isArray(cart.items) || typeof cart.active !== 'boolean' || !Number.isInteger(cart.earned) || cart.earned < 0 || !cart.campaign) throw Error('Could not verify gift allowance');
      return { ...cart, token: typeof native?.token === 'string' && native.token.trim() ? native.token : null };
    },
    change: (key, quantity) => post('cart/change.js', { id: key, quantity }),
    add: (id, properties) => post('cart/add.js', { items: [{ id, quantity: 1, properties }] }),
    notify: notifyCart, waitForCart, onState: renderAll,
    refreshChoices: () => loadChoices(false, true, true),
    getAutoCandidates,
  });
  document.addEventListener('click', captureGiftRemoval, true);
  document.addEventListener('change', captureGiftRemoval, true);
  document.addEventListener('blur', captureGiftRemoval, true);
  if (!customElements.get('choice-gift-picker')) customElements.define('choice-gift-picker', class extends HTMLElement {
    connectedCallback() {
      if (!pickerObservers.has(this)) pickerObservers.set(this, new MutationObserver(() => {
        if (this.isConnected) renderPicker(this);
      }));
      renderPicker(this);
    }
    disconnectedCallback() { pickerObservers.get(this)?.disconnect(); }
  });
  if (!customElements.get('choice-gift-header')) customElements.define('choice-gift-header', class extends HTMLElement {
    connectedCallback() {
      if (!headerObservers.has(this)) headerObservers.set(this, new MutationObserver(() => {
        if (this.isConnected) renderHeader(this);
      }));
      renderHeader(this);
    }
    disconnectedCallback() { headerObservers.get(this)?.disconnect(); }
  });
  document.addEventListener('click', (event) => {
    const opener = event.target.closest?.('[data-choice-gift-open]');
    if (opener) { event.preventDefault(); openPicker(opener); return; }
    const lineOpener = event.target.closest?.('[data-choice-line-toggle]');
    if (lineOpener && !lineOpener.disabled) { event.preventDefault(); const host = findPicker(lineOpener.dataset.choiceLineToggle); if (host) openLinePicker(host); return; }
    const picker = event.target.closest?.('choice-gift-picker');
    if (!picker) return;
    const id = picker.dataset.pickerId;
    const retry = event.target.closest('[data-choice-retry]');
    if (retry && !retry.disabled) { manager.refresh().catch(() => {}).finally(() => focusPicker(id)); return; }
    const close = event.target.closest('[data-choice-close]');
    if (close && !close.disabled) { openPickers.delete(id); renderAll(); focusPicker(id); return; }
    const more = event.target.closest('[data-choice-more]');
    if (more && !more.disabled) { loadChoices(Boolean(loadedChoices)); return; }
    const replacement = event.target.closest('[data-choice-replace]');
    if (replacement && !replacement.disabled) {
      const selectedVariant = replacement.closest('.choice-gift__product').querySelector('select')?.value || replacement.dataset.choiceReplace;
      manager.replace(replacement.dataset.choiceFrom, Number(selectedVariant)).then(() => {
        if (!manager.error && manager.messageKey === 'changed') openPickers.delete(id);
        renderAll();
      }).catch(() => {}).finally(() => focusPicker(id, Number(selectedVariant)));
      return;
    }
    const add = event.target.closest('[data-choice-add]');
    if (add && !add.disabled) {
      const variantId = add.closest('.choice-gift__product').querySelector('select')?.value;
      manager.claim(Number(variantId)).then(() => {
        if (!manager.error && manager.messageKey === 'added') openPickers.delete(id);
        renderAll();
      }).catch(() => {}).finally(() => focusPicker(id, Number(variantId)));
    }
  });
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const picker = event.target.closest?.('choice-gift-picker');
    if (!picker || !openPickers.has(picker.dataset.pickerId)) return;
    event.preventDefault(); event.stopPropagation();
    openPickers.delete(picker.dataset.pickerId); renderAll(); focusPicker(picker.dataset.pickerId);
  }, true);
  document.addEventListener('change', (event) => {
    if (event.target.matches?.('[data-choice-product]')) { variantSelections.set(event.target.dataset.choiceProduct, event.target.value); renderAll(); }
  });
  const schedule = (event, force = false) => {
    if (event?.detail?.data?.source === SOURCE) return;
    schedule.force = schedule.force || force;
    window.clearTimeout(schedule.timer);
    schedule.timer = window.setTimeout(async () => {
      const refresh = schedule.force; schedule.force = false;
      try { await manager.refresh(); await loadChoices(false, refresh); }
      catch (_) { setCatalogState('unknown'); }
    }, 180);
  };
  document.addEventListener(ThemeEvents.cartUpdate, schedule);
  document.addEventListener(ThemeEvents.discountUpdate, schedule);
  document.addEventListener(DialogOpenEvent.eventName, (event) => {
    if (event.target.matches?.('cart-drawer-component')) schedule(null, true);
  }, true);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') schedule(null, true);
  });
  window.addEventListener('pageshow', (event) => { if (event.persisted) schedule(null, true); });
  setCatalogState('unknown');
  document.addEventListener('click', interceptCheckout, true);
  document.addEventListener('submit', interceptCheckout, true);
  schedule();
  const intent = new URL(window.location.href).searchParams;
  if (intent.get('choice_gift_open') === '1' && document.querySelector('choice-gift-picker')) {
    openPicker({ dataset: { choiceGiftVariant: intent.get('choice_gift_variant') || '' } });
  }
}

initChoiceGiftPicker();
